// Bugsnag must be imported first to catch initialization errors
import '@/lib/bugsnag'
// Register service worker with idle-reload + navigation-reload on update
import { isUpdatePending, applyUpdate } from '@/lib/sw-update'

import React, { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from '@/lib/auth'
import { initSessionRestore } from '@/lib/session-restore'
import { routeTree } from './routeTree.gen'
import { NotFound } from '@/components/not-found'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { Capacitor } from '@capacitor/core'
import Bugsnag from '@bugsnag/js'
import './index.css'

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: false,
  defaultNotFoundComponent: NotFound,
  context: {
    queryClient: undefined!,
    auth: undefined!,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// On navigation: apply pending SW update (reload with new assets)
router.subscribe('onBeforeNavigate', () => {
  if (isUpdatePending()) {
    applyUpdate()
  }
})

// Leave navigation breadcrumbs in Bugsnag
if (Bugsnag.isStarted()) {
  router.subscribe('onResolved', (state) => {
    Bugsnag.leaveBreadcrumb('Navigation', {
      to: state.toLocation.pathname,
      search: state.toLocation.searchStr,
    }, 'navigation')
  })
}

// Record the organizer's location so a native cold start can put them
// back into their event (see src/lib/session-restore.ts).
initSessionRestore(router)

// Bugsnag error boundary (passthrough if Bugsnag not started)
const BugsnagErrorBoundary = Bugsnag.isStarted()
  ? Bugsnag.getPlugin('react')!.createErrorBoundary(React)
  : ({ children }: { children: React.ReactNode }) => <>{children}</>

function ErrorFallback() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif' }}>
      <h1>Something went wrong</h1>
      <p>The app encountered an unexpected error. Please try refreshing the page.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '1rem',
          padding: '0.5rem 1rem',
          borderRadius: '0.375rem',
          border: '1px solid #d1d5db',
          cursor: 'pointer',
        }}
      >
        Refresh
      </button>
    </div>
  )
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function InnerApp() {
  const auth = useAuth()
  
  // Notify Capgo that the app is ready
  useEffect(() => {
    CapacitorUpdater.notifyAppReady()
  }, [])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    document.body.classList.add('native-safe-area')
    return () => {
      document.body.classList.remove('native-safe-area')
    }
  }, [])
  
  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        auth,
      }}
    />
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </QueryClientProvider>
  )
}

const rootElement = document.getElementById('root')!

// Prevent creating multiple roots during HMR
let root: ReturnType<typeof createRoot>
if (!(rootElement as any)._reactRoot) {
  root = createRoot(rootElement)
  ;(rootElement as any)._reactRoot = root
} else {
  root = (rootElement as any)._reactRoot
}

root.render(
  <StrictMode>
    <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
      <App />
    </BugsnagErrorBoundary>
  </StrictMode>,
)
